import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { CHECK_FIRST_DELAY, CHECK_SECOND_DELAY, CHANGE_DELAY_AFTER, ANALYSIS_STATUS } from '@/consts';
import { isEmpty, runInterval } from '@/utils/miscUtils';
import { cancelAnalysis, getAnalysisStatus } from '@/api/analyses';
import { subscribeWithSelector } from 'zustand/middleware';
import { resourcesActions } from './resourcesStore';

const initialValues = {
  analysisId: null,
  pathwayId: null,
  isStale: false,
  analysisTypes: {},
};

const useAnalysisStatusStore = create()(subscribeWithSelector(immer(() => initialValues)));
const { setState: set, getState: get } = useAnalysisStatusStore;

export const subscriptions = [];
const intervals = [];

// actions
export const analysisStatusActions = {
  init: async caseData => {
    const {
      analysis_id: analysisId,
      pathway_id: pathwayId,
      analysis_status: initStatus,
      analysis_stale: isStale,
    } = caseData;

    analysisStatusActions.clear();

    set({ analysisId, pathwayId, isStale });

    if (initStatus) {
      set(state => {
        Object.entries(initStatus).forEach(([type, status]) => {
          state.analysisTypes[type] = { status };
        });
      });

      resourcesActions.updatePathway({ id: pathwayId, analysis_status: initStatus });
    }

    Object.entries(initStatus ?? {}).forEach(([type, status]) => {
      if (
        status !== ANALYSIS_STATUS.succeeded &&
        status !== ANALYSIS_STATUS.failed &&
        status !== ANALYSIS_STATUS.cancelled
      ) {
        analysisStatusActions.checkStatusPeriodically(type);
      } else {
        analysisStatusActions.checkStatus(type);
      }
    });
  },

  addSubscription: subscription => {
    subscriptions.push(subscription);
  },

  clearSubscriptions: () => {
    subscriptions.forEach(unsubscribe => unsubscribe());
    subscriptions.length = 0;
  },

  clearIntervals: () => {
    intervals.forEach(interval => interval());
    intervals.length = 0;
  },

  checkStatus: async analysisType => {
    const { analysisId, pathwayId } = get();

    if (!analysisId) {
      return false;
    }

    try {
      const { data } = await getAnalysisStatus(analysisId, analysisType);
      const { status } = data ?? {};

      set(state => {
        state.analysisTypes[analysisType] = data;
      });

      resourcesActions.updatePathway({
        id: pathwayId,
        analysis_status: { [analysisType]: status },
        analysis_stale: false,
      });

      if (
        status === ANALYSIS_STATUS.succeeded ||
        status === ANALYSIS_STATUS.failed ||
        status === ANALYSIS_STATUS.cancelled
      ) {
        return true;
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        return false;
      }

      set(state => {
        state.analysisTypes[analysisType] = {
          status: ANALYSIS_STATUS.failed,
        };
      });

      console.error('error', err);
      return true;
    }

    return false;
  },

  checkStatusPeriodically: analysisType => {
    const stopInterval = runInterval(
      () => analysisStatusActions.checkStatus(analysisType),
      CHECK_FIRST_DELAY,
      CHECK_SECOND_DELAY,
      CHANGE_DELAY_AFTER,
    );
    intervals.push(stopInterval);
  },

  markAsStale: () => {
    const { pathwayId } = get();
    set({ isStale: true });

    resourcesActions.updatePathway({ id: pathwayId, analysis_stale: true });
  },

  isInProgress: () => {
    const { analysisTypes } = get();

    if (isEmpty(analysisTypes)) {
      return false;
    }

    return !!Object.values(analysisTypes).find(
      type =>
        type.status !== ANALYSIS_STATUS.succeeded &&
        type.status !== ANALYSIS_STATUS.failed &&
        type.status !== ANALYSIS_STATUS.cancelled,
    );
  },

  cancelAnalysis: async () => {
    const { analysisId } = get();
    await cancelAnalysis(analysisId);
  },

  clear: () => {
    set({ analysisTypes: {} });
    analysisStatusActions.clearSubscriptions();
    analysisStatusActions.clearIntervals();
  },
};

// selectors
export const useIsAnalysisStale = () => useAnalysisStatusStore(store => store.isStale);

export const useIsLCAAndTEASucceeded = () =>
  useAnalysisStatusStore(store => {
    const { analysisTypes } = store;

    return (
      analysisTypes?.lca?.status === ANALYSIS_STATUS.succeeded &&
      analysisTypes?.tea?.status === ANALYSIS_STATUS.succeeded
    );
  });

export const useIsLCAAndTEAStatus = () => {
  return useAnalysisStatusStore(store => {
    const { analysisTypes } = store;

    if (
      analysisTypes?.lca?.status === ANALYSIS_STATUS.succeeded &&
      analysisTypes?.tea?.status === ANALYSIS_STATUS.succeeded
    ) {
      return ANALYSIS_STATUS.succeeded;
    }

    if (
      analysisTypes?.lca?.status === ANALYSIS_STATUS.failed ||
      analysisTypes?.tea?.status === ANALYSIS_STATUS.failed
    ) {
      return ANALYSIS_STATUS.failed;
    }

    if (
      analysisTypes?.lca?.status === ANALYSIS_STATUS.cancelled ||
      analysisTypes?.tea?.status === ANALYSIS_STATUS.cancelled
    ) {
      return ANALYSIS_STATUS.cancelled;
    }

    return ANALYSIS_STATUS.running;
  });
};

export const useIsAnalysisInProgress = () =>
  useAnalysisStatusStore(store => {
    const { analysisTypes } = store;

    if (isEmpty(analysisTypes)) {
      return false;
    }

    return !!Object.values(analysisTypes).some(
      type =>
        type.status !== ANALYSIS_STATUS.succeeded &&
        type.status !== ANALYSIS_STATUS.failed &&
        type.status !== ANALYSIS_STATUS.cancelled,
    );
  });

export const useIsAnalysisFailed = () =>
  useAnalysisStatusStore(store => {
    const { analysisTypes } = store;
    return !!Object.values(analysisTypes).find(type => type.status === ANALYSIS_STATUS.failed);
  });

export const useAnalysisErrors = () =>
  useAnalysisStatusStore(store => {
    const { analysisTypes } = store;
    return Object.entries(analysisTypes).reduce((acc, [type, { status, error }]) => {
      if (status === ANALYSIS_STATUS.failed) {
        acc[type] = error;
      }
      return acc;
    }, {});
  });

export const useAnalysisProgress = analysisType =>
  useAnalysisStatusStore(store => store.analysisTypes?.[analysisType]?.progress);
export const useAnalysisTypes = () => useAnalysisStatusStore(store => store.analysisTypes);

export default useAnalysisStatusStore;
